import type { SubmitEventHandler } from 'react';
import type {
  ApiKey,
  ContentTypeDefinition,
  GeneratedForm,
} from '../../lib/flotiq-client/types/api-response';

import {
  use,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { setFormSubmitErrors } from '../../form-hooks/lib/helpers';
import { EMPTY_ARRAY } from '../../lib/globals';
import { getTestProps } from '../../lib/helpers';

import Button from '../../components/Button/Button';
import CancelButton from '../../components/Button/predefined/CancelButton/CancelButton';
import SaveButton from '../../components/Button/predefined/SaveButton/SaveButton';
import Tabs from '../../components/Tabs/Tabs';

import { ModalInstanceContext } from '../../contexts/ModalContext';

import { PencilIcon } from '../../images/shapes';

import { useAppForm } from '../../form-hooks/form';
import Advanced, { MESSAGE_FIELDS } from './tabs/Advanced/Advanced';
import Basic from './tabs/Basic/Basic';
import FormScripts from './tabs/Scripts/Scripts';

export type FormGeneratorValues = {
  origin: string;
  flotiqApiKey: string;
  config: Record<string, any>;
  messages: Record<string, any>;
};

interface FormGeneratorModalFormProps {
  /**
   * Content type for which form is displayed
   */
  contentType: ContentTypeDefinition;
  /**
   * On form submit callback
   */
  onSubmit: (
    value: FormGeneratorValues,
  ) => Promise<Record<string, string | string[]> | undefined>;
  /**
   * On generate API key button click
   */
  generateApiKey: () => Promise<ApiKey | null>;
  /**
   * Generated form data
   */
  generatedForm?: GeneratedForm | null;
  /**
   * User api keys
   */
  apiKeys?: ApiKey[];
  /**
   * Component test id
   */
  testId?: string;
}

const getConfig = (config?: string | Record<string, string | string[]>) => {
  return typeof config === 'string' ? JSON.parse(config || '{}') : config || {};
};

const getInitialValues = (generatedForm?: GeneratedForm | null) => ({
  origin: generatedForm?.origin || '*',
  flotiqApiKey: generatedForm?.flotiqApiKey || '',
  config: getConfig(generatedForm?.config),
  messages: getConfig(generatedForm?.messages),
});

const FormGeneratorModalForm = ({
  contentType,
  onSubmit,
  generateApiKey,
  generatedForm,
  apiKeys = EMPTY_ARRAY,
  testId = '',
}: FormGeneratorModalFormProps) => {
  const { t } = useTranslation();
  const modalInstance = use(ModalInstanceContext);

  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(true);
  const [apiKeyGenerated, toggleApiKeyGenerated] = useReducer(
    (isGenerated) => !isGenerated,
    false,
  );

  useEffect(() => {
    if (generatedForm) {
      setIsEditing(false);
    }
  }, [generatedForm]);

  const isCloseModal = !generatedForm || !isEditing;

  const onCancel = useCallback(() => {
    if (!isCloseModal) setIsEditing(false);
    else modalInstance.resolve?.();
  }, [isCloseModal, modalInstance]);

  const validationSchema = useMemo(
    () =>
      z.object({
        origin: z.string().min(1, t('Form.FormErrorNotBlank')),
        flotiqApiKey: z.string().min(1, t('Form.FormErrorNotBlank')),
        config: z.object({
          recaptchaPublic: z.string().optional(),
          recaptchaPrivate: z.string().optional(),
          maxFiles: z.number().optional(),
          allowedTypes: z.array(z.string()).optional(),
        }),
        messages: z.object(
          MESSAGE_FIELDS.reduce(
            (acc, field) => ({
              ...acc,
              [field]: z.string().optional(),
            }),
            {},
          ),
        ),
      }),
    [t],
  );

  const onGenerateApiKey = useCallback(async () => {
    const apiKey = await generateApiKey();
    if (apiKey) toggleApiKeyGenerated();
    return apiKey;
  }, [generateApiKey]);

  const backToEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  const form = useAppForm({
    defaultValues: getInitialValues(generatedForm),
    onSubmit: async ({ value, formApi }) => {
      setIsSaving(true);

      const errors = await onSubmit(value);
      if (!errors) {
        setIsEditing(false);
      } else {
        setFormSubmitErrors(formApi, errors);
      }

      setIsSaving(false);
    },
    validators: {
      onChange: validationSchema,
    },
  });

  const handleSubmit = useCallback<SubmitEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    },
    [form],
  );

  const configTabs = useMemo(
    () => [
      {
        label: t('ContentTypeForm.FormGenerator.Basic'),
        key: 'basic',
        render: () => (
          <Basic
            apiKeys={apiKeys}
            onGenerateApiKey={onGenerateApiKey}
            apiKeyGenerated={apiKeyGenerated}
            contentType={contentType}
            form={form}
            testId={testId}
          />
        ),
      },
      {
        label: t('ContentTypeForm.FormGenerator.Advanced'),
        key: 'advanced',
        render: () => <Advanced form={form} testId={testId} />,
      },
    ],
    [apiKeyGenerated, apiKeys, contentType, form, onGenerateApiKey, t, testId],
  );

  return (
    <form.AppForm>
      <form
        id="generated-form"
        onSubmit={handleSubmit}
        noValidate
        {...getTestProps(testId, 'form')}
      >
        {isEditing ? (
          <Tabs tabs={configTabs} testId={testId} />
        ) : (
          <FormScripts formUrl={generatedForm?.formUrl} testId={testId} />
        )}

        <div
          className="w-full absolute left-0 bottom-0 flex items-center justify-center p-3 space-x-5
            border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900"
        >
          <CancelButton
            onClick={onCancel}
            label={!isCloseModal ? t('Global.Cancel') : t('Global.Close')}
            {...getTestProps(testId, 'cancel', 'testId')}
          />

          {isEditing ? (
            <SaveButton
              form="generated-form"
              isSaving={isSaving}
              {...getTestProps(testId, 'save', 'testId')}
            />
          ) : (
            <Button
              buttonColor="blue"
              onClick={backToEdit}
              iconImage={<PencilIcon className="w-3 min-w-3" />}
              iconPosition="start"
              {...getTestProps(testId, 'edit-settings', 'testId')}
            >
              {t('ContentTypeForm.FormGenerator.EditFormSettings')}
            </Button>
          )}
        </div>
      </form>

      <form.ValidationToastHandler hideToast />
    </form.AppForm>
  );
};

export default FormGeneratorModalForm;
